import React from 'react'
import { toast as sonner } from 'sonner'
import { MdEmail } from 'react-icons/md'

// Simple error boundary for toast rendering
const createSafeToast = (toastFn) => (...args) => {
  try {
    return toastFn(...args)
  } catch (error) {
    console.error('Toast rendering failed:', {
      error,
      args,
      toastName: toastFn.name
    })
  }
}

export const clearAllToasts = () => {
  try {
    sonner?.dismiss()
    return true
  } catch (error) {
    console.error('Failed to clear toasts:', error)
    return false
  }
}

export const AppUpdateToast = ({
  children,
  setIsNewVersion,
}) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="font-semibold">App Update</div>
      <p className="text-sm text-muted-foreground m-0">{children}</p>
      <div className="flex justify-end">
        <button
          data-cy="toast_update_button"
          onClick={() => {
            setIsNewVersion(true).then(() => {
              window.location.reload()
            }).catch(error => {
              console.error('Failed to update version:', error)
              sonner.error('Failed to update. Please refresh manually.')
            })
          }}
          className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors"
        >
          Update
        </button>
      </div>
    </div>
  )
}

const createToast = (text) => (
  <div className="flex flex-col gap-1">
    <p className="text-sm text-foreground m-0">{text}</p>
  </div>
)

const CANCELABLE_IDS = {
  info: null,
  success: null,
  ghost: null,
}

const DEFAULT_TOAST_CONFIG = {
  position: 'bottom-left',
  duration: 4000,
  className: 'group',
  dismissible: true,
  containerAriaLabel: 'Notifications',
  onDismiss: (toast) => {
    console.debug('Toast dismissed:', toast?.id)
  },
  onAutoClose: (toast) => {
    console.debug('Toast auto-closed:', toast?.id)
  },
  actionButtonStyle: {
    backgroundColor: 'var(--primary)',
    color: 'var(--primary-foreground)',
  },
  cancelButtonStyle: {
    backgroundColor: 'var(--secondary)',
    color: 'var(--secondary-foreground)',
  }
}

const toastImplementation = {
  /**
   * Dismiss a specific toast by ID
   * @param {string} toastId - ID of toast to dismiss
   */
  dismiss: (toastId) => {
    if (toastId) {
      sonner?.dismiss(toastId)
    }
  },

  /**
   * Display a success toast message
   * @param {string} text - The message to display
   */
  success: (text) => {
    CANCELABLE_IDS.success && sonner?.dismiss(CANCELABLE_IDS.success)
    CANCELABLE_IDS.success = sonner.success(createToast(text), DEFAULT_TOAST_CONFIG)
  },

  /**
   * Display an error toast message
   * @param {string} text - The error message to display
   */
  error: (text) => {
    sonner.error(createToast(text), DEFAULT_TOAST_CONFIG)
  },

  /**
   * Display an info toast message
   * @param {string} text - The info message to display
   * @param {boolean} [isFixed=false] - If true, the toast will be fixed at top-right and won't auto-dismiss
   */
  info: (text, isFixed = false) => {
    CANCELABLE_IDS.info && sonner?.dismiss(CANCELABLE_IDS.info)
    CANCELABLE_IDS.info = sonner(createToast(text), {
      ...DEFAULT_TOAST_CONFIG,
      duration: isFixed ? Infinity : DEFAULT_TOAST_CONFIG.duration,
      position: isFixed ? 'top-right' : DEFAULT_TOAST_CONFIG.position,
    })
  },

  /**
   * Display a custom toast with custom content and options
   * @param {React.ReactNode} content - The custom content to display
   * @param {Object} [options={}] - Custom options to override DEFAULT_TOAST_CONFIG
   * @returns {string} Toast ID
   */
  custom: (content, options = {}) => {
    return sonner.custom(content, {
      ...DEFAULT_TOAST_CONFIG,
      ...options,
    })
  },

  /**
   * Display a thread update notification toast
   * @param {Function} onUpdate - Callback function to execute when user clicks "See them now!"
   * @returns {string} Toast ID
   */
  threadUpdate: (onUpdate) => {
    const toastId = 'thread-update-' + Date.now()
    return sonner(
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 font-semibold">
          <MdEmail /> You have new messages!
        </div>
        <p className="text-sm text-muted-foreground m-0">
          We know you're working, so we'll wait until you're ready to
          update your messages.
        </p>
        <div className="flex justify-end">
          <button
            onClick={() => {
              try {
                onUpdate()
                sonner?.dismiss(toastId)
              } catch (error) {
                console.error('Failed to update threads:', error)
                sonner.error('Failed to update messages')
              }
            }}
            className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors"
          >
            See them now!
          </button>
        </div>
      </div>,
      {
        duration: Infinity,
        position: 'top-right',
        id: toastId
      }
    )
  },

  /**
   * Display a promise-based toast with loading, success, and error states
   * @param {Promise<any>} promise - The promise to track
   * @param {Object} messages - Custom messages for different states
   * @param {string} [messages.loading] - Message to show while loading
   * @param {string} [messages.success] - Message to show on success
   * @param {string} [messages.error] - Message to show on error
   * @returns {string} Toast ID
   */
  promise: (promise, messages) => {
    return sonner.promise(promise, {
      ...DEFAULT_TOAST_CONFIG,
      loading: messages?.loading || 'Loading...',
      success: messages?.success || 'Completed successfully',
      error: messages?.error || 'An error occurred',
    })
  },

  /**
   * Display a ghost mode notification toast
   */
  ghostMode: () => {
    CANCELABLE_IDS.ghost && sonner?.dismiss(CANCELABLE_IDS.ghost)
    const body = (
      <div className="flex flex-col gap-2">
        <span className="font-semibold">You are in ghost mode</span>
        <p className="text-sm text-muted-foreground m-0">
          Note: You are browsing ResiDesk silently. Messages you read
          will not affect your team's unread messages. Messages you
          read will also not be cleared from your inbox.
        </p>
      </div>
    )
    CANCELABLE_IDS.ghost = sonner(body, {
      duration: Infinity,
      position: 'top-right',
    })
  },

  /**
   * Display an authentication error toast
   * @returns {string} Toast ID
   */
  authError: () => {
    sonner?.dismiss()
    return sonner(
      <div className="flex flex-col gap-2">
        <span className="font-semibold">
          For your safety, you have been logged out. Please refresh
          your browser to login again.
        </span>
      </div>,
      {
        duration: Infinity,
        position: 'top-right',
        id: 'auth-error'
      }
    )
  },

  /**
   * Display a new version notification toast
   * @param {string} text - The notification message
   * @param {Object} options - Options for the version update
   * @param {boolean} [options.isUpdate=false] - Whether this is an update notification
   * @param {Function} [options.setIsNewVersion] - Function to set new version state
   * @param {Function} [options.setNewReleaseReset] - Function to reset new release state
   * @returns {string} Toast ID
   */
  newVersion: (
    text,
    {
      isUpdate = false,
      setIsNewVersion = () => Promise.resolve(),
      setNewReleaseReset = () => null,
    },
  ) => {
    if (isUpdate) {
      return sonner(
        <AppUpdateToast
          setIsNewVersion={setIsNewVersion}
          setNewReleaseReset={setNewReleaseReset}
        >
          {text}
        </AppUpdateToast>,
        {
          duration: Infinity,
          position: 'top-right',
          id: 'version-update'
        }
      )
    } else {
      return sonner(text, DEFAULT_TOAST_CONFIG)
    }
  },

  /**
   * Display a perpetual action toast with customizable buttons
   * @param {string} text - The main message text
   * @param {Object} options - Options for the action toast
   * @param {Function} [options.runner] - Function to run when primary action is clicked
   * @param {string} [options.actionText='Ok'] - Text for the primary action button
   * @param {string} [options.headerText='Note'] - Header text for the toast
   * @param {Array<{text: string, onClick: Function}>} [options.additionalActions=[]] - Additional action buttons
   * @returns {string} Toast ID
   */
  perpetualAction: (
    text,
    {
      runner = () => Promise.resolve(),
      actionText = 'Ok',
      headerText = 'Note',
      additionalActions = [],
    } = {},
  ) => {
    const toastId = 'perpetual-' + Date.now()
    return sonner(
      <div className="flex flex-col gap-2">
        <h3 className="text-base font-semibold">{headerText}</h3>
        <p className="text-sm text-muted-foreground m-0">{text}</p>
        <div className="flex justify-end gap-2">
          {additionalActions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                try {
                  action?.onClick()
                } catch (error) {
                  console.error('Action execution failed:', error)
                  sonner.error('Failed to execute action')
                }
              }}
              className="px-3 py-1.5 text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-md transition-colors"
            >
              {action.text}
            </button>
          ))}
          <button
            onClick={async () => {
              try {
                await runner()
                sonner?.dismiss(toastId)
              } catch (error) {
                console.error('Runner execution failed:', error)
                sonner.error('Failed to execute action')
              }
            }}
            className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors"
          >
            {actionText}
          </button>
        </div>
      </div>,
      {
        duration: Infinity,
        id: toastId
      }
    )
  },

  /**
   * Display a toast with suggested responses
   * @param {Array<{response: string, reason: string}>} responses - Array of suggested responses
   * @param {Object} options - Options for the suggestions toast
   * @param {string} [options.headerText="Friday's Suggested Responses"] - Header text
   * @param {Function} [options.responseFillAction] - Function to call when a response is selected
   * @returns {string} Toast ID
   */
  suggestedResponses: (
    responses,
    {
      headerText = `Friday suggests these responses based on this conversation and guidelines`,
      responseFillAction = null,
    } = {},
  ) => {
    const toastId = 'suggested-responses-' + Date.now()
    return sonner(
      <div className="flex flex-col gap-3 max-w-[300px]">
        <h3 className="text-base font-semibold">{headerText}</h3>
        <div className="h-[60vh] overflow-auto">
          <div className="text-sm text-muted-foreground mb-2">Based on this conversation and guidelines:</div>
          <ul className="space-y-4 list-none p-0">
            {responses?.map(({ response, reason }, index) => (
              <li key={index} className="border-b border-border pb-4 last:border-0">
                <div className="font-medium mb-1">Response:</div>
                <p className="text-sm text-muted-foreground mb-2">{response}</p>
                <div className="font-medium mb-1">Reason:</div>
                <p className="text-sm text-muted-foreground mb-2">{reason}</p>
                <button
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                  onClick={() => {
                    try {
                      responseFillAction?.(response)
                      sonner?.dismiss(toastId)
                    } catch (error) {
                      console.error('Failed to fill response:', error)
                      sonner.error('Failed to use response')
                    }
                  }}
                >
                  Use Response
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>,
      {
        duration: Infinity,
        id: toastId
      }
    )
  },
}

/**
 * Toast utility with error handling for all methods
 * @type {Object.<string, Function>}
 * @property {(text: string) => void} success - Display a success toast message
 * @property {(text: string) => void} error - Display an error toast message
 * @property {(text: string, isFixed?: boolean) => void} info - Display an info toast message
 * @property {(content: React.ReactNode, options?: Object) => string} custom - Display a custom toast
 * @property {(onUpdate: Function) => string} threadUpdate - Display a thread update notification
 * @property {(promise: Promise<any>, messages?: {loading?: string, success?: string, error?: string}) => string} promise - Display a promise-based toast
 * @property {() => void} ghostMode - Display a ghost mode notification
 * @property {() => string} authError - Display an authentication error toast
 * @property {(text: string, options: {isUpdate?: boolean, setIsNewVersion?: Function, setNewReleaseReset?: Function}) => string} newVersion - Display a new version notification
 * @property {(text: string, options?: {runner?: Function, actionText?: string, headerText?: string, additionalActions?: Array<{text: string, onClick: Function}>}) => string} perpetualAction - Display a perpetual action toast
 * @property {(responses: Array<{response: string, reason: string}>, options?: {headerText?: string, responseFillAction?: Function}) => string} suggestedResponses - Display suggested responses
 */
const toast = Object.fromEntries(
  Object.entries(toastImplementation).map(([key, fn]) => [key, createSafeToast(fn)])
)

export default toast
